import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Skeleton,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import type { PaletteColor, Theme } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import {
  CheckCircleOutline,
  ChevronRight,
  HomeWorkOutlined,
  SavingsOutlined,
} from '@mui/icons-material';
import { listings } from '@prisma/client';
import offerReasoningService, {
  OfferReasoning,
  OfferScenario,
  ScenarioKey,
} from '../../../api/services/offerReasoning';
import { formatCurrency } from '../../../utils/formatCurrency';
import useIsMobile from '../../../hooks/useIsMobile';

interface Props {
  listing: listings;
  /** Which scenario is currently selected, if the buyer has picked one. */
  selectedKey: ScenarioKey | null;
  /** Called when a scenario is picked, to pre-fill the wizard. */
  onSelectScenario: (scenario: OfferScenario) => void;
  /** Advance to the next wizard step. */
  onContinue: () => void;
}

/**
 * The theme's warn colour. `Palette.highlight` is declared as a string in
 * src/augmentations.d.ts but is created with `augmentColor`, so it is a real
 * PaletteColor at runtime.
 */
const warnColor = (theme: Theme) =>
  (theme.palette.highlight as unknown as PaletteColor).main;

/** Meter fill colour by posture: warmer for riskier, greener for safer. */
const meterFill = (theme: Theme, key: ScenarioKey) => {
  if (key === 'AGGRESSIVE') return warnColor(theme);
  if (key === 'CONSERVATIVE') return theme.palette.success.main;
  return theme.palette.primary.main;
};

const SectionHeading = ({
  title,
  subtitle,
  subtitleTestId,
}: {
  title: string;
  subtitle: string;
  subtitleTestId?: string;
}) => (
  <Box>
    <Typography variant="subtitle1" fontWeight={700}>
      {title}
    </Typography>
    <Typography
      variant="body2"
      color="text.secondary"
      data-testid={subtitleTestId}
    >
      {subtitle}
    </Typography>
  </Box>
);

const StatBox = ({
  value,
  label,
  detail,
  testId,
}: {
  value: string;
  label: string;
  detail: string;
  testId: string;
}) => (
  <Box
    data-testid={testId}
    sx={(theme) => ({
      p: 1.5,
      height: '100%',
      borderRadius: 1.5,
      bgcolor: alpha(theme.palette.secondary.main, 0.04),
      border: `1px solid ${alpha(theme.palette.secondary.main, 0.12)}`,
    })}
  >
    <Typography
      variant="subtitle1"
      fontWeight={800}
      sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}
    >
      {value}
    </Typography>
    <Typography variant="caption" fontWeight={600} display="block">
      {label}
    </Typography>
    <Typography variant="caption" color="text.secondary" display="block">
      {detail}
    </Typography>
  </Box>
);

const WinProbabilityMeter = ({ scenario }: { scenario: OfferScenario }) => {
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box
          data-testid={`scenario-meter-${scenario.key}`}
          role="meter"
          aria-label={`Modeled win probability for ${scenario.label}`}
          aria-valuenow={scenario.winProbability}
          aria-valuemin={0}
          aria-valuemax={100}
          sx={(theme) => ({
            flex: 1,
            height: 6,
            borderRadius: 3,
            bgcolor: alpha(theme.palette.text.primary, 0.08),
            overflow: 'hidden',
          })}
        >
          <Box
            sx={(theme) => ({
              width: `${scenario.winProbability}%`,
              height: '100%',
              borderRadius: 3,
              bgcolor: meterFill(theme, scenario.key),
            })}
          />
        </Box>
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {scenario.winProbability}%
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Modeled win probability
      </Typography>
    </Box>
  );
};

const ScenarioCard = ({
  scenario,
  isSelected,
  onSelect,
}: {
  scenario: OfferScenario;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  const borderAlpha = isSelected ? 1 : 0.16;
  const backgroundAlpha = isSelected ? 0.07 : 0;

  return (
    <Card
      elevation={0}
      data-testid={`scenario-card-${scenario.key}`}
      data-selected={isSelected ? 'true' : 'false'}
      role="radio"
      aria-label={`${scenario.label} offer: ${formatCurrency(
        scenario.price
      )}, ${scenario.winProbability}% modeled win probability. ${scenario.tradeOffs.join(
        '. '
      )}`}
      aria-checked={isSelected}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
      sx={(theme) => ({
        cursor: 'pointer',
        width: '100%',
        borderRadius: 2,
        bgcolor: alpha(theme.palette.primary.main, backgroundAlpha),
        border: `${isSelected ? 3 : 1}px solid ${alpha(
          theme.palette.primary.main,
          borderAlpha
        )}`,
        boxShadow: isSelected
          ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`
          : 'none',
        transition: 'border-color 120ms ease, box-shadow 120ms ease',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.4),
        },
      })}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack spacing={1}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1}
          >
            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ letterSpacing: 1, lineHeight: 1.4 }}
              >
                {scenario.label}
              </Typography>
              <Typography
                data-testid={`scenario-price-${scenario.key}`}
                fontSize={24}
                fontWeight={800}
                sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}
              >
                {formatCurrency(scenario.price)}
              </Typography>
            </Box>
          </Stack>

          <WinProbabilityMeter scenario={scenario} />

          <Stack spacing={0.5}>
            {scenario.tradeOffs.map((tradeOff) => (
              <Stack
                key={tradeOff}
                direction="row"
                spacing={1}
                alignItems="flex-start"
              >
                <CheckCircleOutline
                  fontSize="small"
                  sx={{ color: 'text.secondary', mt: '2px' }}
                />
                <Typography variant="body2" color="text.secondary">
                  {tradeOff}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

const TermRow = ({
  label,
  value,
  testId,
  onEdit,
}: {
  label: string;
  value: string;
  testId: string;
  onEdit: () => void;
}) => (
  <Box
    component="button"
    type="button"
    data-testid={testId}
    onClick={onEdit}
    sx={(theme) => ({
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
      px: 2,
      py: 1.5,
      background: 'none',
      border: 0,
      borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
      cursor: 'pointer',
      textAlign: 'left',
      font: 'inherit',
      color: 'inherit',
      '&:last-of-type': { borderBottom: 0 },
    })}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Typography
        variant="body2"
        fontWeight={700}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </Typography>
      <ChevronRight fontSize="small" sx={{ color: 'text.disabled' }} />
    </Stack>
  </Box>
);

const OfferReasoningStep: React.FC<Props> = ({
  listing,
  selectedKey,
  onSelectScenario,
  onContinue,
}) => {
  const isMobile = useIsMobile();
  const [reasoning, setReasoning] = useState<OfferReasoning | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await offerReasoningService.find(listing.id);
        if (cancelled) return;
        setReasoning(data);
      } catch {
        if (cancelled) return;
        setError(
          'We could not model offer scenarios for this home right now. You can still set your own price and terms.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [listing.id]);

  const handleSelect = useCallback(
    (scenario: OfferScenario) => {
      onSelectScenario(scenario);
    },
    [onSelectScenario]
  );

  if (loading) {
    return (
      <Stack spacing={2} data-testid="offer-reasoning-skeleton">
        <Skeleton variant="rounded" height={150} />
        <Skeleton variant="text" width="45%" />
        <Skeleton variant="rounded" height={168} />
        <Skeleton variant="rounded" height={168} />
        <Skeleton variant="rounded" height={168} />
      </Stack>
    );
  }

  if (error || !reasoning) {
    return (
      <Stack spacing={2} data-testid="offer-reasoning-error">
        <Alert severity="info">{error}</Alert>
        <Button variant="contained" onClick={onContinue} fullWidth>
          Continue to your offer
        </Button>
      </Stack>
    );
  }

  const { snapshot, scenarios, buyingPower } = reasoning;
  const activeScenario = selectedKey
    ? scenarios.find((scenario) => scenario.key === selectedKey) ?? null
    : null;
  // Until Homepoint confirms what the seller is actually offering a buyer's
  // broker, this is an assumption off a placeholder rate — say so rather than
  // presenting it as savings the buyer already has.
  const buyingPowerCopy = buyingPower.commissionVerified
    ? `You're keeping about ${formatCurrency(
        buyingPower.amount
      )} in commission savings — put it toward price, closing costs, or a rate buydown.`
    : `If the seller offers the ${
        buyingPower.commissionPercent
      }% commission we've assumed, that's about ${formatCurrency(
        buyingPower.amount
      )} of buying power for price, closing costs, or a rate buydown. ` +
      'Homepoint confirms the offered commission before your offer goes out.';
  const domDetail = snapshot.medianDaysOnMarketSold
    ? `ZIP median is ${snapshot.medianDaysOnMarketSold} for sold homes`
    : 'No ZIP median available';
  const hasValueRange = Boolean(snapshot.valueRange);
  const scenariosSubtitle = hasValueRange
    ? 'Reva modeled three scenarios from comps and market activity. ' +
      'Tap one to fill in the wizard — every field stays editable after.'
    : 'Not enough recent sales nearby to value this home, so these scenarios ' +
      'are anchored to the list price and market activity. ' +
      'Tap one to fill in the wizard — every field stays editable after.';
  const valueRangeText = snapshot.valueRange
    ? `${formatCurrency(snapshot.valueRange.low)}–${formatCurrency(
        snapshot.valueRange.high
      )}`
    : 'Not available';

  return (
    <Stack spacing={3} data-testid="offer-reasoning-step">
      {reasoning.degraded && (
        <Alert severity="info" data-testid="offer-reasoning-degraded">
          {reasoning.degradedReason} Treat these as a starting point and adjust
          anything before you submit.
        </Alert>
      )}

      {/* Snapshot */}
      <Card
        elevation={0}
        data-testid="offer-snapshot"
        sx={(theme) => ({
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
        })}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="flex-start"
            justifyContent="space-between"
            mb={2}
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              minWidth={0}
            >
              <Box
                sx={(theme) => ({
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.main,
                })}
              >
                <HomeWorkOutlined fontSize="small" />
              </Box>
              <Box minWidth={0}>
                <Typography variant="body2" fontWeight={700} noWrap>
                  {reasoning.address}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {reasoning.cityStateZip}
                </Typography>
              </Box>
            </Stack>
            <Box textAlign="right" flexShrink={0}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ letterSpacing: 1, lineHeight: 1.4 }}
              >
                List price
              </Typography>
              <Typography
                data-testid="snapshot-list-price"
                variant="subtitle1"
                fontWeight={800}
                sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}
              >
                {formatCurrency(snapshot.listPrice)}
              </Typography>
            </Box>
          </Stack>

          <Grid2 container spacing={1.5}>
            <Grid2 xs={6}>
              <StatBox
                testId="snapshot-dom"
                value={
                  snapshot.daysOnMarket === null
                    ? '—'
                    : `${snapshot.daysOnMarket} days`
                }
                label="On market"
                detail={domDetail}
              />
            </Grid2>
            <Grid2 xs={6}>
              <StatBox
                testId="snapshot-list-to-sale"
                value={
                  snapshot.listToSaleRatio === null
                    ? '—'
                    : `${snapshot.listToSaleRatio}%`
                }
                label="List-to-sale ratio"
                detail="Across sold homes in this ZIP"
              />
            </Grid2>
            <Grid2 xs={6}>
              <StatBox
                testId="snapshot-competing-offers"
                value={`${snapshot.competingOfferCount}`}
                label={
                  snapshot.competingOfferCount === 1
                    ? 'Other offer'
                    : 'Other offers'
                }
                detail="Reported on this listing"
              />
            </Grid2>
            <Grid2 xs={6}>
              <StatBox
                testId="snapshot-value-range"
                value={valueRangeText}
                label="Estimated value"
                detail={
                  hasValueRange
                    ? "Reva's range from comps"
                    : 'Not enough recent sales nearby'
                }
              />
            </Grid2>
          </Grid2>
        </CardContent>
      </Card>

      {/* Scenarios */}
      <Stack spacing={1.5}>
        <SectionHeading
          title="Choose your offer"
          subtitle={scenariosSubtitle}
          subtitleTestId="offer-scenarios-subtitle"
        />
        <Grid2 container spacing={1.5} role="radiogroup" aria-label="Offer scenarios">
          {scenarios.map((scenario) => (
            <Grid2 key={scenario.key} xs={12} sm={4} display="flex">
              <ScenarioCard
                scenario={scenario}
                isSelected={scenario.key === selectedKey}
                onSelect={() => handleSelect(scenario)}
              />
            </Grid2>
          ))}
        </Grid2>
      </Stack>

      {/* Buying power — only when the offered commission actually clears our
          flat fee; below that there is nothing to put toward the offer. */}
      {buyingPower.hasBuyingPower && (
        <Card
          elevation={0}
          data-testid="offer-buying-power"
          sx={(theme) => ({
            borderRadius: 2,
            bgcolor: alpha(warnColor(theme), 0.07),
            border: `1px solid ${alpha(warnColor(theme), 0.28)}`,
          })}
        >
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box
                sx={(theme) => ({
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(warnColor(theme), 0.16),
                  color: warnColor(theme),
                })}
              >
                <SavingsOutlined fontSize="small" />
              </Box>
              <Box>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ letterSpacing: 1, lineHeight: 1.4 }}
                >
                  Buying power
                </Typography>
                <Typography variant="body2">{buyingPowerCopy}</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Terms */}
      {activeScenario && (
        <Stack spacing={1.5}>
        <SectionHeading
          title="Offer terms"
          subtitle={`Defaults set from the ${activeScenario.label} scenario — change anything before you submit.`}
        />
        <Card
          elevation={0}
          data-testid="offer-terms"
          sx={(theme) => ({
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
          })}
        >
          <TermRow
            testId="offer-term-earnest-money"
            label="Earnest money"
            value={`${formatCurrency(activeScenario.terms.earnestMoney)} (${
              activeScenario.terms.earnestMoneyPercent
            }%)`}
            onEdit={onContinue}
          />
          <TermRow
            testId="offer-term-inspection"
            label="Inspection period"
            value={`${activeScenario.terms.inspectionPeriod} days`}
            onEdit={onContinue}
          />
          <TermRow
            testId="offer-term-financing"
            label="Financing"
            value={activeScenario.terms.paymentMethod}
            onEdit={onContinue}
          />
          <TermRow
            testId="offer-term-concessions"
            label="Seller concessions"
            value={formatCurrency(activeScenario.terms.concessions)}
            onEdit={onContinue}
          />
          <TermRow
            testId="offer-term-closing"
            label="Closing"
            value={`${activeScenario.terms.closingDays} days`}
            onEdit={onContinue}
          />
        </Card>
        </Stack>
      )}

      <Divider />

      <Box>
        <Button
          variant="contained"
          size="large"
          fullWidth
          data-testid="offer-reasoning-cta"
          onClick={onContinue}
          disabled={!activeScenario}
          sx={{ fontWeight: 700, py: isMobile ? 1.25 : 1.5 }}
        >
          {activeScenario
            ? `Continue with ${activeScenario.label} — ${formatCurrency(
                activeScenario.price
              )}`
            : 'Select an offer to continue'}
        </Button>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          textAlign="center"
          mt={1}
        >
          Choose the offer that fits you. You can edit every term on the next
          screen.
        </Typography>
      </Box>
    </Stack>
  );
};

export default OfferReasoningStep;
